import type { TextEffectsTextSectionFields } from '../TextEffects/TextEffectsShared/editor/panels/design/TextEffectsShared.designPanel.types';
import { AnimationType } from './viewer/TextParticles.types';

export const ViewerType = 'wixui.TextParticles' as const;
export const DataType = 'TextParticlesData' as const;
export const SkinType = 'wixui.skins.TextParticles' as const;

export const TestIds = {
  viewer: {
    canvas: 'text-particles-canvas',
    link: 'text-particles-link',
    text: 'text-particles-text',
    animationState: 'data-animation-state',
  },
} as const;

export const DEFAULT_RADIUS = 2;
export const DEFAULT_TEXT_DIRECTION = 'inherit' as const;

// Target total emitted effect particle amount throughout reveal animation
export const TARGET_TOTAL_PARTICLE_COUNT = 15000;

// Threshold for image sizes to determine which block size to be used for revealing image
export const LARGE_IMAGE_THRESHOLD = 250000;
export const MEDIUM_IMAGE_THRESHOLD = 100000;

// NxN pixel block sizes
export const LARGE_IMAGE_BLOCK_SIZE = 3;
export const MEDIUM_IMAGE_BLOCK_SIZE = 2;
export const SMALL_IMAGE_BLOCK_SIZE = 1;

export const EFFECT_PARTICLE_MIN_LIFETIME = 100;
export const MIN_EFFECT_PARTICLES_PER_BLOCK = 1;
export const MAX_EFFECT_PARTICLES_PER_BLOCK = 2;

// Presets are designed for 30px
export const PRESET_BASE_FONT_SIZE = 30;

export const CANVAS_WIDTH_MULTIPLIER = 3;
// Text is usually wider than taller making the spread area an elongated rectangle
// Height multiplier is increased to make the spread area more square-ish
export const CANVAS_HEIGHT_MULTIPLIER = 5;

export const dataHooks = {
  settingsPanel: {
    textInput: 'textparticles-text-input',
    textDirection: 'textparticles-text-direction',
    textDirectionToggle: 'textparticles-text-direction-toggle',
  },
  designPanel: {
    movementSection: 'movement-section',
    animationDirectionSetting: 'animation-direction-setting',
    windDirectionSlider: 'wind-direction-slider',
    speedSlider: 'speed-slider',
    intensitySlider: 'intensity-slider',
    triggerAnimationButton: 'trigger-animation-button',
    animationDurationSlider: 'textparticles-animation-duration-slider',
    particleAdjustmentSection: 'particle-adjustment-section',
    animationTypeDropdown: 'animation-type-dropdown',
    root: 'text-particles-design-panel',
    particleSizeInput: 'particle-size-input',
    particleOpacityInput: 'particle-opacity-input',
    particleColorCountInput: 'particle-color-count-input',
    particleColorSwatches: 'particle-color-swatches',
    textColorInput: 'text-color-input',
    effectTypeSetting: 'effect-type-setting',
    noiseSlider: 'noise-slider',
    amplitudeSlider: 'amplitude-slider',
    startPositionSetting: 'start-position-setting',
    effectPresetSetting: 'effect-preset-setting',
    particlesEffectTypePrefix: 'particles-effect-type-',
    particleDurationSlider: 'particle-duration-slider',
    pauseOnHoverSetting: 'pause-on-hover-setting',
  },
};

export const translationKeys = {
  designPanel: {
    movementSectionTitle:
      'Inspector-TextParticles.Section-Design.Movement-Section-Title',
    previewAnimationLabel:
      'Inspector-TextParticles.Section-Design.Play-Animation-Label',
    revealDirectionLabel:
      'Inspector-TextParticles.Section-Design.Direction-Dropdown',
    windDirectionLabel:
      'Inspector-TextParticles.Section-Design.Wind-Direction-Label',
    speedLabel: 'Inspector-TextParticles.Section-Design.Speed-Label',
    intensityLabel: 'Inspector-TextParticles.Section-Design.Intensity-Label',
    durationLabel: 'Inspector-TextParticles.Section-Design.Duration-Label',
    particleAdjustmentSectionTitle:
      'Inspector-TextParicles.Section-Design.Particles-Effect-Section-Label',
    animationTypeLabel:
      'Inspector-TextParticles.Section-Design.Animation-Type-Label',
    animationTypeIn: 'Inspector-TextParticles.Section-Design.Animation-Type-In',
    animationTypeOut:
      'Inspector-TextParticles.Section-Design.Animation-Type-Out',
    particleStartSizeLabel:
      'Inspector-TextParticles.Section-Design.Particle-Start-Size-Label',
    particleEndSizeLabel:
      'Inspector-TextParticles.Section-Design.Particle-End-Size-Label',
    particleSizeLabel:
      'Inspector-TextParticles.Section-Design.Particle-Size-Label',
    particleSizeInfo:
      'Inspector-TextParticles.Section-Design.Particle-Size-Tooltip',
    particleOpacityInfo:
      'Inspector-TextParticles.Section-Design.Particle-Opacity-Tooltip',
    particleStartOpacityLabel:
      'Inspector-TextParticles.Section-Design.Particle-Start-Opacity-Label',
    particleEndOpacityLabel:
      'Inspector-TextParticles.Section-Design.Particle-End-Opacity-Label',
    particleOpacityLabel:
      'Inspector-TextParticles.Section-Design.Particle-Opacity-Label',
    particleColorCountLabel:
      'Inspector-TextParticles.Section-Design.Particle-Color-Count-Label',
    colorSectionTitle:
      'Inspector-TextParticles.Section-Design.Color-Section-Title',
    textColorLabel: 'Inspector-TextParticles.Section-Design.Text-Color-Label',
    directionFromLeft: 'Inspector-TextParticles.Section-Design.Direction-Left',
    directionFromRight:
      'Inspector-TextParticles.Section-Design.Direction-Right',
    directionFromTop:
      'Inspector-TextParticles.Section-Design.Direction-Top-Center',
    directionFromBottom:
      'Inspector-TextParticles.Section-Design.Direction-Bottom',
    directionFromTopLeft:
      'Inspector-TextParticles.Section-Design.Direction-Top-Left',
    directionFromTopRight:
      'Inspector-TextParticless.Section-Design.Direction-Top-Right',
    directionFromBottomLeft:
      'Inspector-TextParticles.Section-Design.Direction-Bottom-Left',
    directionFromBottomRight:
      'Inspector-TextParticles.Section-Design.Direction-Bottom-Right',
    startPositionTop:
      'Inspector-TextParticles.Section-Design.Start-Position-Top',
    startPositionBottom:
      'Inspector-TextParticles.Section-Design.Start-Position-Bottom',
    startPositionLeft:
      'Inspector-TextParticles.Section-Design.Start-Position-Left',
    startPositionRight:
      'Inspector-TextParticles.Section-Design.Start-Position-Right',
    startPositionTopLeft:
      'Inspector-TextParticles.Section-Design.Start-Position-Top-Left',
    startPositionTopRight:
      'Inspector-TextParticles.Section-Design.Start-Position-Top-Right',
    startPositionBottomLeft:
      'Inspector-TextParticles.Section-Design.Start-Position-Bottom-Left',
    startPositionBottomRight:
      'Inspector-TextParticles.Section-Design.Start-Position-Bottom-Right',
    startPositionCenter:
      'Inspector-TextParticles.Section-Design.Start-Position-Center',
    startPositionRandom:
      'Inspector-TextParticles.Section-Design.Start-Position-Random',
    startPositionLabel:
      'Inspector-TextParticles.Section-Design.Start-Position-Label',
    noiseLabel: 'Inspector-TextParticles.Section-Design.Noise-Label',
    amplitudeLabel: 'Inspector-TextParticles.Section-Design.Amplitude-Label',
    particleDurationLabel:
      'Inspector-TextParticles.Section-Design.Particle-Duration-Label',
    presetRevealLabel:
      'Inspector-TextParticles.Section-Design.Preset-Reveal-Label',
    presetWaveLabel: 'Inspector-TextParticles.Section-Design.Preset-Wave-Label',
    presetSwarmLabel:
      'Inspector-TextParticles.Section-Design.Preset-Swarm-Label',
    patternLabel: 'Inspector-TextParticles.Section-Design.Pattern-Label',
    patternWaveChillLabel:
      'Inspector-TextParticles.Section-Design.Pattern-Wave-Chill',
    patternWaveLivelyLabel:
      'Inspector-TextParticles.Section-Design.Pattern-Wave-Lively',
    patternWaveWildLabel:
      'Inspector-TextParticles.Section-Design.Pattern-Wave-Wild',
    patternRevealSparkleLabel:
      'Inspector-TextParticles.Section-Design.Pattern-Reveal-Sparkle',
    patternRevealFlameLabel:
      'Inspector-TextParticles.Section-Design.Pattern-Reveal-Flame',
    patternRevealOutLabel:
      'Inspector-TextParticles.Section-Design.Pattern-Reveal-Out',
    patternSwarmEnterLabel:
      'Inspector-TextParticles.Section-Design.Pattern-Swarm-Enter',
    patternSwarmManifestLabel:
      'Inspector-TextParticles.Section-Design.Pattern-Swarm-Manifest',
    patternSwarmAppearLabel:
      'Inspector-TextParticles.Section-Design.Pattern-Swarm-Appear',
    pauseOnHoverLabel:
      'Inspector-TextParticles.Section-Design.Pause-On-Hover-Label',
  },
  settingsPanel: {
    title: 'TextParticles.Settings_Title',
    textFieldLabel: 'TextParticles.Settings_Text-Field-Label',
    textFieldPlaceholder: 'TextParticles.Setting_Text-Field_Placeholder',
    changeDirection: 'TextParticles.Settings_ChangeDirection-Field-Label',
    leftToRight:
      'TextParticles.Settings_TextBox_Direction_LeftToRight_Label_Hover',
    rightToLeft:
      'TextParticles.Settings_TextBox_Direction_RightToLeft_Label_Hover',
  },
  gfpp: {
    changeText: 'gfpp_MainAction_TextParticles.ChangeText',
    help: 'TextParticles_help_gfpp',
  },
  dataBindingPanel: {
    title: 'Connect_Panel_Title_TextParticles',
    componentName: 'TextParticles_db_comp_name',
    linkActionTitle: 'Connect_Panel_Connect_TextParticles_Link_Label',
    textConnectLabel: 'Connect_Panel_Connect_TextParticles_Label',
    manageContentPanelDescription: 'TextParticles_db_manage_content',
    gfppActionLabel: 'TextParticles_db_edit_text_label',
  },
};

export const helpIds = {
  settings: 'cb95e8ac-7a2d-4674-8fbc-98d3c034dc88',
  gfpp: 'cb95e8ac-7a2d-4674-8fbc-98d3c034dc88',
};

export const SKIN_PARAM_RANGES = {
  duration: { min: 0.5, max: 5, step: 0.1 },
  'particle-start-size': { min: 0.1, max: 10, step: 0.1 },
  'particle-end-size': { min: 0.1, max: 10, step: 0.1 },
  'particle-start-opacity': { min: 0, max: 100, step: 1 },
  'particle-end-opacity': { min: 0, max: 100, step: 1 },
  'wind-direction': { min: 0, max: 360, step: 1 },
  speed: { min: 0, max: 10, step: 0.1 },
  turbulence: { min: 0, max: 10, step: 0.1 },
  'particle-color-count': { min: 1, max: 4, step: 1 },
  'particle-duration': { min: 0, max: 5, step: 0.1 },
  amplitude: { min: 0, max: 10, step: 0.1 },
  noise: { min: 0, max: 10, step: 0.1 },
  intensity: { min: 0, max: 10, step: 0.1 },
} as const;

export const TEXT_PARTICLES_TEXT_SECTION_FIELDS: TextEffectsTextSectionFields =
  {
    theme: true,
    fontSize: true,
    fontFamily: true,
    spacing: true,
    textStyles: true,
    textTransform: true,
    textAlign: ['start', 'center', 'end'],
    headingTag: true,
    textDirection: true,
  };

export const TEXT_PARTICLES_DEFAULT_TEXT = 'TextParticles';

export const SWARM_ANIMATION_DEFAULTS = {
  [AnimationType.IN]: {
    startSize: 0,
    endSize: 1,
    startOpacity: 0,
    endOpacity: 100,
  },
  [AnimationType.OUT]: {
    startSize: 1,
    endSize: 0,
    startOpacity: 100,
    endOpacity: 0,
  },
} as const;
